import { Lightning, Router, Utils, VideoPlayer } from '@lightningjs/sdk'

import { MediaButton, NowPlaying } from '../components'
import { Socket, Touch } from '../utils'

const lightThemes = ['classicfm', 'lbcnews']

export default class Player extends Lightning.Component {
  // Lifecycle
  _init() {
    this.index = 0
    this.theme = 'dark'
    this.socketInstance = new Socket('wss://metadata.musicradio.com/v2/now-playing')
    this.socketInstance.onMessage = data => {
      this.handleMetadata(data)
    }
    this.socketInstance.connect()
    this.tag('Artwork').on('txError', () => {
      this.tag('Artwork').patch({
        src: Utils.asset('default_presenter.png'),
      })
    })

    const touchHandler = new Touch()
    touchHandler.enable(this.stage)
  }

  _firstActive() {
    VideoPlayer.consumer(this)
  }

  _active() {
    this.index = 0
    VideoPlayer.open(this.item.nationalStation.streamUrl)
    this.tag('BrandLogo').patch({
      src: this.item.tvLogo.url,
    })
    this.tag('Artwork').patch({
      src: Utils.asset('default_presenter.png'),
    })
    this.tag('Background').patch({
      color: 0xffffffff,
      src: this.item.background.url,
    })
    this.theme = lightThemes.indexOf(this.item.theme) >= 0 ? 'light' : 'dark'
    // Special override for LBC News :(
    if (this.item.name == 'LBC News') {
      this.theme = 'light'
    }
    if (this.theme == 'light') {
      this.patch({
        Stack: {
          Logo: {
            src: Utils.asset('logo-small-light.png'),
          },
          ShowName: {
            text: {
              textColor: 0xff4a4a4a,
            },
          },
          ShowTimes: {
            text: {
              textColor: 0xff4a4a4a,
            },
          },
          NowPlaying: {
            textColor: 0xff4a4a4a,
          },
        },
      })
    } else {
      this.patch({
        Stack: {
          Logo: {
            src: Utils.asset('logo-small-dark.png'),
          },
          ShowName: {
            text: {
              textColor: 0xffffffff,
            },
          },
          ShowTimes: {
            text: {
              textColor: 0xffffffff,
            },
          },
          NowPlaying: {
            textColor: 0xffffffff,
          },
        },
      })
    }
    this.socketInstance.subscribe(this.item.nationalStation.heraldId)
  }

  _inactive() {
    this.socketInstance.unsubscribe(this.item.nationalStation.heraldId)
    VideoPlayer.close()
    this.stage.gc()
  }

  _detach() {
    this.socketInstance.close()
  }

  // Template
  static _template() {
    return {
      Background: {
        rect: true,
        color: 0xff006efa,
        w: 1920,
        h: 1080,
      },
      Stack: {
        flex: { direction: 'column' },
        x: 60,
        y: 60,
        h: 960,
        w: 1140,
        Logo: {
          flexItem: {},
          src: Utils.asset('logo-small-light.png'),
          w: 271,
          h: 55,
        },
        BrandLogo: {
          flexItem: { marginTop: 24 },
        },
        ShowName: {
          flexItem: { marginTop: 24 },
          w: 1140,
          text: {
            text: 'Loading...',
            fontFace: 'Regular',
            fontSize: 56,
            fontStyle: 'bold',
            textColor: 0xffffffff,
          },
        },
        ShowTimes: {
          flexItem: {},
          text: {
            text: 'Loading...',
            fontFace: 'Regular',
            fontSize: 28,
            textColor: 0xffffffff,
          },
        },
        NowPlaying: {
          flexItem: { marginTop: 24 },
          h: 150,
          type: NowPlaying,
          textColor: 0xffffffff,
        },
        MediaButtons: {
          x: -12,
          flexItem: { marginTop: 48 },
          flex: { direction: 'row' },
          PlayButton: {
            type: MediaButton,
            buttonIcon: Utils.asset('play-icon.png'),
          },
          BackButton: {
            type: MediaButton,
            buttonIcon: Utils.asset('back-icon.png'),
          },
        },
      },
      Artwork: {
        w: 600,
        h: 600,
        x: 1260,
        y: 60,
        color: 0xffffffff,
        src: Utils.asset('default_presenter.png'),
        zIndex: 2,
      },
      ArtworkBackground: {
        w: 600,
        h: 600,
        x: 1260,
        y: 60,
        color: 0x33000000,
        rect: true,
        zIndex: 1,
      },
    }
  }

  // Getters / Setters
  set params(args) {
    this.brand = args.brand
    this.item = args.item
  }

  _getFocused() {
    return this.tag('MediaButtons').children[this.index]
  }

  // Util Methods
  handleMetadata(data) {
    if (data.current_show) {
      this.tag('ShowName').patch({
        text: { text: data.current_show.name },
      })
      this.tag('ShowTimes').patch({
        text: { text: data.current_show.schedule },
      })
      if (data.now_playing.type == 'track') {
        this.tag('Artwork').patch({
          src: data.now_playing.proxied_artwork,
        })
      } else {
        this.tag('Artwork').patch({
          src: data.now_playing.artwork_with_background,
        })
      }
      this.tag('NowPlaying').updateInfo(data.now_playing)
    }
    this.stage.gc()
  }

  // Event Handlers
  _handleLeft() {
    if (this.index > 0) {
      this.index--
    }
  }

  _handleRight() {
    if (this.index < 1) {
      this.index++
    }
  }

  _handlePlay() {
    VideoPlayer.play()
  }

  _handlePause() {
    VideoPlayer.pause()
  }

  _handleStop() {
    VideoPlayer.pause()
  }

  _handlePlayPause() {
    if (VideoPlayer.playing) {
      VideoPlayer.pause()
    } else {
      VideoPlayer.play()
    }
  }

  _handleEnter() {
    if (this.index == 1) {
      Router.navigate('home')
    }
    if (this.index == 0) {
      this._handlePlayPause()
    }
  }

  _handleBack() {
    Router.navigate('home')
    return false
  }

  _handleTouch(element) {
    for (const [itemIndex, item] of this.tag('MediaButtons').children.entries()) {
      if (element.__id == item.__id) {
        this.index = itemIndex
        this._handleEnter()
      }
    }
  }

  $videoPlayerEvent(eventName) {
    if (eventName == 'Playing') {
      this.tag('MediaButtons').children[0].updateIcon(Utils.asset('stop-icon.png'))
    }
    if (eventName == 'Pause') {
      this.tag('MediaButtons').children[0].updateIcon(Utils.asset('play-icon.png'))
    }
  }
}
